import React, { useEffect, useState } from "react";
import client, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button, Input, Card, Modal, Badge } from "./ui";
import { FlaskConical, Link2, KeyRound, RotateCcw, MessageSquarePlus } from "lucide-react";

function PwModal({ acct, open, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPw(acct?.password || ""); setErr(""); } }, [open, acct]);
  const save = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await client.put("/admin/demo/password", { username: acct.username, password: pw });
      onSaved(data); onClose();
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Modal open={open} onClose={onClose} title={`Change password — ${acct?.name || ""}`} testid="demo-pw-modal">
      <div className="space-y-3 text-sm">
        <p className="opacity-70">Account <b>{acct?.username}</b>. This password stays visible in the demo catalog.</p>
        <Input label="New password (min 6 chars)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="demo-pw-input" />
        {err && <p className="text-[#DA1E28]" data-testid="demo-pw-error">{err}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || pw.length < 6} data-testid="demo-pw-save">Save password</Button>
        </div>
      </div>
    </Modal>
  );
}

function Row({ acct, onEdit }) {
  const [copied, setCopied] = useState("");
  const copy = (txt, what) => { navigator.clipboard?.writeText(txt); setCopied(what); setTimeout(() => setCopied(""), 1400); };
  const origin = window.location.origin;
  const link = `${origin}/demo-login?launch=${acct.username}`;
  const invite = `Hi — here's a live, no-sign-up demo of ${acct.name} from Full Option Family Office.\n\n`
    + `Launch it instantly: ${link}\n\n`
    + `(Or go to ${origin}/demo-login and sign in with username "${acct.username}" and password "${acct.password}".)\n\n`
    + `It's a fully sandboxed simulation — explore freely, nothing is live.`;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-black/10 px-3 py-2 text-sm dark:border-white/10"
      data-testid={`demo-admin-row-${acct.username}`}>
      <div className="min-w-0 flex-1">
        <div className="font-medium">{acct.name}</div>
        <div className="flex flex-wrap gap-x-3 text-xs opacity-70">
          <span>user: <b>{acct.username}</b></span>
          <span>pass: <b>{acct.password}</b></span>
          <button className="text-[var(--primary)] hover:underline" onClick={() => copy(link, "link")}
            data-testid={`demo-admin-share-${acct.username}`}><Link2 size={11} className="inline" /> copy share link</button>
          <button className="text-[var(--primary)] hover:underline" onClick={() => copy(invite, "invite")}
            data-testid={`demo-admin-invite-${acct.username}`}><MessageSquarePlus size={11} className="inline" /> copy invite</button>
          {copied && <span className="text-[var(--primary)]">copied {copied}!</span>}
        </div>
      </div>
      <Button variant="ghost" onClick={() => onEdit(acct)} data-testid={`demo-admin-edit-${acct.username}`}>
        <KeyRound size={14} className="me-1" /> Password
      </Button>
    </div>
  );
}

export default function DemoAccountsPanel() {
  const { isAdmin } = useApp();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => client.get("/demo/accounts").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);
  if (!isAdmin) return null;

  const onSaved = () => { setMsg("Password updated."); load(); setTimeout(() => setMsg(""), 2000); };
  const reseed = async () => {
    if (!window.confirm("Recreate ALL demo accounts with fresh data? Custom passwords will be reset to defaults.")) return;
    setBusy(true);
    try { await client.post("/admin/demo/reseed"); await load(); setMsg("Demo accounts recreated."); }
    catch { setMsg("Reseed failed."); }
    setBusy(false); setTimeout(() => setMsg(""), 2500);
  };

  return (
    <Card className="p-5" data-testid="demo-accounts-panel">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <FlaskConical size={18} className="text-[var(--primary)]" />
        <h3 className="font-head text-lg">Demo accounts</h3>
        <Badge color="info">sales &amp; publicity</Badge>
        {msg && <span className="text-xs text-[var(--primary)]" data-testid="demo-admin-msg">{msg}</span>}
        <a href="/demo-login" target="_blank" rel="noreferrer" className="ms-auto text-xs text-[var(--primary)] hover:underline" data-testid="demo-admin-open">Open demo centre ↗</a>
        <Button variant="ghost" onClick={reseed} disabled={busy} data-testid="demo-admin-reseed">
          <RotateCcw size={14} className="me-1" /> {busy ? "Working…" : "Reseed all"}
        </Button>
      </div>
      <p className="mb-4 text-xs opacity-60">Change any demo password or copy a one-click share link. Passwords remain visible so the sales team can hand them out.</p>
      {!data && <p className="opacity-60 text-sm">Loading…</p>}
      {data && (
        <div className="space-y-4">
          {data.single_apps.map((app) => (
            <div key={app.app_type}>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wider opacity-50">{app.label}</div>
              <div className="grid gap-2 md:grid-cols-2">
                {app.accounts.map((a) => <Row key={a.username} acct={a} onEdit={setEditing} />)}
              </div>
            </div>
          ))}
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider opacity-50">Full suite</div>
            <div className="grid gap-2 md:grid-cols-2">
              {data.suite.accounts.map((a) => <Row key={a.username} acct={a} onEdit={setEditing} />)}
            </div>
          </div>
        </div>
      )}
      <PwModal acct={editing} open={!!editing} onClose={() => setEditing(null)} onSaved={onSaved} />
    </Card>
  );
}
